import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Button } from "@mui/material"
import type { FC } from "react"
import React from "react"
import { FormProvider, useForm } from "react-hook-form"

import { CancelDialog } from "@/components/common/Modals"
import type { PriceChangeType } from "@/graphql/codegen/graphql"
import PriceChangeBulkOptionsForm from "@/screens/Pricing/components/forms/PriceChangeBulkOptionsForm"
import { PricingDialogs, PricingNumericTypeValues } from "@/screens/Pricing/constants"
import { usePricingDialog } from "@/screens/Pricing/hooks/usePricingDialog"
import type { BulkUpdateFormValues } from "@/screens/Pricing/schema/BulkUpdateSchema"
import { BulkUpdateSchema } from "@/screens/Pricing/schema/BulkUpdateSchema"

interface BulkUpdateOptionsProps {
  onApply: (formValues: BulkUpdateFormValues) => void
  onClear?: () => void
  readonly?: boolean
  priceChangeType?: PriceChangeType
  hasAppliedBulkUpdate?: boolean
}

const BulkUpdateOptions: FC<BulkUpdateOptionsProps> = ({
  onApply,
  onClear,
  readonly,
  priceChangeType,
  hasAppliedBulkUpdate,
}) => {
  const { isDialogOpen, setIsDialogOpen } = usePricingDialog()

  const methods = useForm({
    defaultValues: {
      acceptedCost: null,
      acceptedCostType: PricingNumericTypeValues.Percent,
      maintainMargin: false,
      priceChange: null,
      priceChangeType: PricingNumericTypeValues.Percent,
      expirationDate: null,
      effectiveDate: null,
    },
    mode: "onChange",
    resolver: zodResolver(BulkUpdateSchema),
  })

  const {
    formState: { isDirty, isValid },
    reset,
    handleSubmit,
  } = methods

  const onSubmitData = (data: BulkUpdateFormValues) => {
    onApply(data)
  }

  const handleClear = () => {
    if (hasAppliedBulkUpdate) {
      setIsDialogOpen(PricingDialogs.ClearBulkUpdateConfirmation)
      return
    }

    handleClearConfirm()
  }

  const handleClearConfirm = () => {
    reset()
    onClear?.()
    setIsDialogOpen(null)
  }

  return (
    <Box className='mt-6 flex w-full flex-row justify-between gap-3'>
      <FormProvider {...methods}>
        <PriceChangeBulkOptionsForm priceChangeType={priceChangeType} />
      </FormProvider>

      <Box className='mt-5 flex flex-row justify-end gap-3 text-nowrap'>
        <Button
          data-testid='pricing.bulk-update.submit-button'
          aria-label='filter'
          className='bg-white capitalize'
          variant='outlined'
          disabled={!isValid || !isDirty || readonly}
          onClick={handleSubmit(onSubmitData)}
        >
          Apply Bulk Update
        </Button>
        <Button className='capitalize' disabled={!isValid || !isDirty} onClick={handleClear}>
          Clear all
        </Button>
      </Box>

      {isDialogOpen === PricingDialogs.ClearBulkUpdateConfirmation && (
        <CancelDialog
          title='Clear bulk updates will discard your changes.'
          message="You've made edits to the current results. Applying different filters will reload the content and remove those changes."
          confirmLabel='Yes, Clear All'
          closeLabel='Cancel'
          onClose={() => setIsDialogOpen(null)}
          onConfirm={handleClearConfirm}
        />
      )}
    </Box>
  )
}
export default BulkUpdateOptions
